import { createAction, createReducer } from "@reduxjs/toolkit"
import { api } from "./helpers/api"
import { find, remove } from "lodash"
import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"
import { toaster } from "components/common/Toast"

const transformPermissionsToObject = (permissions) => {
  if (!permissions) {
    return {}
  }

  const modules = Array.isArray(permissions) ? permissions : Object.values(permissions)

  return modules.reduce((acc, module) => {
    const moduleName = module.access_module_name || module.label?.toLowerCase().replace(/\s+/g, "_") || `module_${module.access_module_id}`

    const settings = module.permission_settings || {}
    const hasScreens = Object.values(settings).some((s) => s && typeof s === "object" && s.actions)

    if (hasScreens) {
      const flatSettings = {}
      Object.values(settings).forEach((screen) => {
        const actions = screen.actions || {}
        Object.entries(actions).forEach(([actionKey, actionData]) => {
          flatSettings[actionKey] = actionData.is_active ? actionData.scope || true : false
        })
      })

      acc[moduleName] = {
        id: module.id,
        access_module_id: module.access_module_id,
        ...flatSettings
      }
    } else {
      acc[moduleName] = {
        id: module.id,
        access_module_id: module.access_module_id,
        view: module.view,
        create: module.create,
        edit: module.edit,
        delete: module.delete,
        full: module.full,
        ...settings
      }
    }
    return acc
  }, {})
}

const transformStaffMemberData = (staffMember) => {
  if (!staffMember) return staffMember

  const normalizeStatus = (status) => {
    if (!status) return ""
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
  }

  return {
    ...staffMember,
    name: staffMember.full_name || `${staffMember.first_name || ""} ${staffMember.last_name || ""}`.trim(),
    title: staffMember.title || "",
    role: staffMember.role?.name || "",
    roleId: staffMember.role?.id || null,
    roleType: staffMember.role?.role_type || null,
    avatar: staffMember.photo_url,
    status: normalizeStatus(staffMember.status),
    documents: staffMember.staff_documents || [],
    availabilities: staffMember.availabilities || [],
    hasFullAccess: staffMember.has_full_access || false,
    isLinked: staffMember.linked || false
  }
}

const enhanceRoleData = (role) => {
  if (!role) return role

  return {
    ...role,
    permissionsMap: transformPermissionsToObject(role.permissions || role.permission_hash),
    isAdmin: role.full_access === true,
    canAssignToBookings: role.role_type === "experience",
    staffCount: role.profile_count || 0
  }
}

const requestedRoles = createAction("staff/roles/requested")
const receivedRoles = createAction("staff/roles/received")
const failedRoles = createAction("staff/roles/failed")

const requestedRole = createAction("staff/role/requested")
const receivedRole = createAction("staff/role/received")
const failedRole = createAction("staff/role/failed")

const createdRole = createAction("staff/role/created")
const updatedRole = createAction("staff/role/updated")
const deletedRole = createAction("staff/role/deleted")

const requestedRoleStaff = createAction("staff/role/staff/requested")
const receivedRoleStaff = createAction("staff/role/staff/received")
const failedRoleStaff = createAction("staff/role/staff/failed")

const requestedStaffMembers = createAction("staff/members/requested")
const receivedStaffMembers = createAction("staff/members/received")
const failedStaffMembers = createAction("staff/members/failed")

const requestedStaffMember = createAction("staff/member/requested")
const receivedStaffMember = createAction("staff/member/received")
const failedStaffMember = createAction("staff/member/failed")

const createdStaffMember = createAction("staff/member/created")
const updatedStaffMember = createAction("staff/member/updated")
const deletedStaffMember = createAction("staff/member/deleted")

const requestedAssignRole = createAction("staff/member/assignRole/requested")
const receivedAssignRole = createAction("staff/member/assignRole/received")
const failedAssignRole = createAction("staff/member/assignRole/failed")

const requestedAssignPermission = createAction("staff/role/assignPermission/requested")
const receivedAssignPermission = createAction("staff/role/assignPermission/received")
const failedAssignPermission = createAction("staff/role/assignPermission/failed")

const initialState = {
  roles: {
    ...LOADING_INITIAL_STATE,
    items: [],
    metadata: {}
  },
  role: {
    ...LOADING_INITIAL_STATE,
    data: null,
    assignedStaff: []
  },
  staffMembers: {
    ...LOADING_INITIAL_STATE,
    items: [],
    metadata: {}
  },
  staffMember: {
    ...LOADING_INITIAL_STATE,
    data: null
  }
}

const staffReducer = createReducer(initialState, {
  [requestedRoles.type]: (state) => {
    setRequested(state.roles)
  },
  [receivedRoles.type]: (state, { payload }) => {
    setReceived(state.roles)
    const rolesArray = payload.data || payload.roles || []
    state.roles.items = rolesArray.map(enhanceRoleData)
    if (payload.metadata) {
      state.roles.metadata = payload.metadata
    }
  },
  [failedRoles.type]: (state) => {
    setFailed(state.roles)
  },

  [requestedRole.type]: (state) => {
    setRequested(state.role)
  },
  [receivedRole.type]: (state, { payload }) => {
    setReceived(state.role)
    const roleData = payload.data || payload
    state.role.data = enhanceRoleData(roleData)
  },
  [failedRole.type]: (state) => {
    setFailed(state.role)
  },

  [createdRole.type]: (state, { payload }) => {
    setReceived(state.role)
    const newRole = enhanceRoleData(payload.data || payload)
    state.roles.items = [newRole, ...state.roles.items]
    state.role.data = newRole
    toaster.success({
      title: "Success",
      text: "Role created successfully"
    })
  },

  [updatedRole.type]: (state, { payload }) => {
    setReceived(state.role)
    const updatedRoleData = enhanceRoleData(payload.data || payload)
    const existingRole = find(state.roles.items, ["id", updatedRoleData.id])
    if (existingRole) {
      Object.assign(existingRole, updatedRoleData)
    }
    state.role.data = updatedRoleData
    toaster.success({
      title: "Success",
      text: "Role updated successfully"
    })
  },

  [deletedRole.type]: (state, { payload }) => {
    setReceived(state.role)
    const roleId = payload.id || payload.data?.id
    remove(state.roles.items, ["id", roleId])
    state.role.data = null
    toaster.success({
      title: "Success",
      text: "Role deleted successfully"
    })
  },

  [requestedRoleStaff.type]: (state) => {
    setRequested(state.role)
  },
  [receivedRoleStaff.type]: (state, { payload }) => {
    setReceived(state.role)
    state.role.assignedStaff = payload.data || payload.staff || []
  },
  [failedRoleStaff.type]: (state) => {
    setFailed(state.role)
  },

  [requestedStaffMembers.type]: (state) => {
    setRequested(state.staffMembers)
  },
  [receivedStaffMembers.type]: (state, { payload }) => {
    setReceived(state.staffMembers)
    const staffArray = payload.data || payload.staff || []
    state.staffMembers.items = staffArray.map(transformStaffMemberData)
    if (payload.metadata) {
      state.staffMembers.metadata = payload.metadata
    }
  },
  [failedStaffMembers.type]: (state) => {
    setFailed(state.staffMembers)
  },

  [requestedStaffMember.type]: (state) => {
    setRequested(state.staffMember)
  },
  [receivedStaffMember.type]: (state, { payload }) => {
    setReceived(state.staffMember)
    const staffData = payload.data || payload
    state.staffMember.data = transformStaffMemberData(staffData)
  },
  [failedStaffMember.type]: (state) => {
    setFailed(state.staffMember)
  },

  [createdStaffMember.type]: (state, { payload }) => {
    setReceived(state.staffMember)
    const newStaff = transformStaffMemberData(payload.data || payload)
    state.staffMembers.items = [newStaff, ...state.staffMembers.items]
    state.staffMember.data = newStaff
    toaster.success({
      title: "Success",
      text: "Staff member created successfully"
    })
  },

  [updatedStaffMember.type]: (state, { payload }) => {
    setReceived(state.staffMember)
    const updatedStaff = transformStaffMemberData(payload.data || payload)
    const existingStaff = find(state.staffMembers.items, ["id", updatedStaff.id])
    if (existingStaff) {
      Object.assign(existingStaff, updatedStaff)
    }
    state.staffMember.data = updatedStaff
    toaster.success({
      title: "Success",
      text: "Staff member updated successfully"
    })
  },

  [deletedStaffMember.type]: (state, { payload }) => {
    setReceived(state.staffMember)
    const staffId = payload.id || payload.data?.id
    remove(state.staffMembers.items, ["id", staffId])
    state.staffMember.data = null
    toaster.success({
      title: "Success",
      text: "Staff member deleted successfully"
    })
  },

  [requestedAssignRole.type]: (state) => {
    setRequested(state.staffMember)
  },
  [receivedAssignRole.type]: (state, { payload }) => {
    setReceived(state.staffMember)
    const updatedStaff = transformStaffMemberData(payload.data || payload)
    const existingStaff = find(state.staffMembers.items, ["id", updatedStaff.id])
    if (existingStaff) {
      Object.assign(existingStaff, updatedStaff)
    }
    if (state.staffMember.data?.id === updatedStaff.id) {
      state.staffMember.data = updatedStaff
    }
    toaster.success({
      title: "Success",
      text: "Role assigned successfully"
    })
  },
  [failedAssignRole.type]: (state) => {
    setFailed(state.staffMember)
  },

  [requestedAssignPermission.type]: () => {},
  [receivedAssignPermission.type]: (state, { payload }) => {
    setReceived(state.role)
    const updatedRoleData = payload.data || payload

    if (state.role.data && state.role.data.id === updatedRoleData.id) {
      Object.assign(state.role.data, {
        ...updatedRoleData,
        permissionsMap: transformPermissionsToObject(updatedRoleData.permission_hash),
        isAdmin: updatedRoleData.full_access === true,
        canAssignToBookings: updatedRoleData.role_type === "experience",
        staffCount: updatedRoleData.profile_count || 0
      })
    } else {
      state.role.data = enhanceRoleData(updatedRoleData)
    }

    const existingRole = find(state.roles.items, ["id", updatedRoleData.id])
    if (existingRole) {
      Object.assign(existingRole, enhanceRoleData(updatedRoleData))
    }
  },
  [failedAssignPermission.type]: (state) => {
    setFailed(state.role)
  }
})

export default staffReducer

export const getRoles = (params = {}) =>
  api({
    url: "/guide/roles",
    method: "get",
    params,
    onStart: requestedRoles.type,
    onSuccess: receivedRoles.type,
    onError: failedRoles.type
  })

export const getRole = (id) =>
  api({
    url: `/guide/roles/${id}`,
    method: "get",
    onStart: requestedRole.type,
    onSuccess: receivedRole.type,
    onError: failedRole.type
  })

export const createRole = (data) =>
  api({
    url: "/guide/roles",
    method: "post",
    data,
    onStart: requestedRole.type,
    onSuccess: createdRole.type,
    onError: failedRole.type
  })

export const updateRole = (id, data) =>
  api({
    url: `/guide/roles/${id}`,
    method: "put",
    data,
    onStart: requestedRole.type,
    onSuccess: updatedRole.type,
    onError: failedRole.type
  })

export const deleteRole = (id, data = {}) =>
  api({
    url: `/guide/roles/${id}`,
    method: "delete",
    data,
    onStart: requestedRole.type,
    onSuccess: deletedRole.type,
    onError: failedRole.type
  })

export const getRoleStaff = (id) =>
  api({
    url: `/guide/roles/${id}/staff`,
    method: "get",
    onStart: requestedRoleStaff.type,
    onSuccess: receivedRoleStaff.type,
    onError: failedRoleStaff.type
  })

export const getStaffMembers = (params = {}) =>
  api({
    url: "/guide/profiles",
    method: "get",
    params,
    onStart: requestedStaffMembers.type,
    onSuccess: receivedStaffMembers.type,
    onError: failedStaffMembers.type
  })

export const getStaffMember = (id) =>
  api({
    url: `/guide/staff/${id}`,
    method: "get",
    onStart: requestedStaffMember.type,
    onSuccess: receivedStaffMember.type,
    onError: failedStaffMember.type
  })

export const createStaffMember = (data) =>
  api({
    url: "/guide/staff",
    method: "post",
    data,
    onStart: requestedStaffMember.type,
    onSuccess: createdStaffMember.type,
    onError: failedStaffMember.type
  })

export const createStaffProfile = (formData) =>
  api({
    url: "/guide/profiles",
    method: "post",
    data: formData,
    onStart: requestedStaffMember.type,
    onSuccess: createdStaffMember.type,
    onError: failedStaffMember.type
  })

export const getStaffProfile = (id) =>
  api({
    url: `/guide/profiles/${id}`,
    method: "get",
    onStart: requestedStaffMember.type,
    onSuccess: receivedStaffMember.type,
    onError: failedStaffMember.type
  })

export const updateStaffProfile = (id, formData) =>
  api({
    url: `/guide/profiles/${id}`,
    method: "put",
    data: formData,
    onStart: requestedStaffMember.type,
    onSuccess: updatedStaffMember.type,
    onError: failedStaffMember.type
  })

export const updateStaffMember = (id, data) =>
  api({
    url: `/guide/staff/${id}`,
    method: "put",
    data,
    onStart: requestedStaffMember.type,
    onSuccess: updatedStaffMember.type,
    onError: failedStaffMember.type
  })

export const deleteStaffMember = (id) =>
  api({
    url: `/guide/staff/${id}`,
    method: "delete",
    onStart: requestedStaffMember.type,
    onSuccess: deletedStaffMember.type,
    onError: failedStaffMember.type
  })

export const assignRoleToStaff = (id, data) =>
  api({
    url: `/guide/staff/${id}/assign_role`,
    method: "post",
    data,
    onStart: requestedAssignRole.type,
    onSuccess: receivedAssignRole.type,
    onError: failedAssignRole.type
  })

export const assignPermissionToRole = (roleId, permissionData) =>
  api({
    url: `/guide/roles/${roleId}/assign_permission`,
    method: "put",
    data: permissionData,
    onStart: requestedAssignPermission.type,
    onSuccess: receivedAssignPermission.type,
    onError: failedAssignPermission.type
  })

export const selectRoles = (state) => state.staff.roles.items
export const selectRolesLoading = (state) => state.staff.roles.loading
export const selectRole = (state) => state.staff.role.data
export const selectRoleLoading = (state) => state.staff.role.loading
export const selectRoleStaff = (state) => state.staff.role.assignedStaff
export const selectStaffMembers = (state) => state.staff.staffMembers.items
export const selectStaffMembersLoading = (state) => state.staff.staffMembers.loading
export const selectStaffMembersMetadata = (state) => state.staff.staffMembers.metadata
export const selectStaffMember = (state) => state.staff.staffMember.data
export const selectStaffMemberLoading = (state) => state.staff.staffMember.loading
export const selectAdminRoles = (state) => state.staff.roles.items.filter((role) => role.isAdmin || role.role_type === "administrative")
export const selectBookingAssignableRoles = (state) => state.staff.roles.items.filter((role) => role.role_type === "experience")
export const selectRoleById = (roleId) => (state) => state.staff.roles.items.find((role) => role.id === roleId)
export const selectRolesWithCount = (state) =>
  state.staff.roles.items.map((role) => ({
    id: role.id,
    name: role.name,
    staffCount: role.staffCount,
    isAdmin: role.isAdmin
  }))
